'use client'

import React, { useState } from 'react'
import { useNavigate, Link, useSearchParams } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { supabase } from '@/integrations/supabase/client'
import EmailVerificationMessage from '@/components/auth/EmailVerificationMessage'
import {
    GraduationCap, School, Users, BookOpen, ChevronRight, ChevronLeft,
    Eye, EyeOff, CheckCircle2, Trophy, ShieldCheck, Activity, Brain,
    Sparkles, ArrowRight, Zap, Heart
} from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { useAuth } from '@/hooks/useAuth'

// ─── Types ────────────────────────────────────────────────────────────────────
type HubType = 'school' | 'family_edu' | 'solo'

interface HubPlan {
    id: HubType
    label: string
    tagline: string
    icon: React.ReactNode
    color: string
    gradient: string
    borderColor: string
    pointsKey: string
    pricing: string
    pricingNote: string
}

// ─── Plan data ─────────────────────────────────────────────────────────────────
const HUB_PLANS: HubPlan[] = [
    {
        id: 'school',
        label: 'School & Classroom',
        tagline: 'Scale excellence across your entire institution',
        icon: <School className="w-8 h-8" />,
        color: '#00a3ff',
        gradient: 'from-[#00a3ff] to-[#1e3a8a]',
        borderColor: 'border-[#00a3ff]',
        pointsKey: 'schools.features',
        pricing: 'Per-Seat Pricing',
        pricingNote: 'From $12/seat/mo · Term billing',
    },
    {
        id: 'family_edu',
        label: 'Family Hub',
        tagline: 'A private AI tutor for every home',
        icon: <Users className="w-8 h-8" />,
        color: '#00a3ff',
        gradient: 'from-[#00a3ff] to-[#00a3ff]',
        borderColor: 'border-[#00a3ff]',
        pointsKey: 'families.features',
        pricing: 'From $35/mo',
        pricingNote: 'Homeschool & Family · Up to 3 learners',
    },
    {
        id: 'solo',
        label: 'Solo Learner',
        tagline: 'Independent mastery for lifelong students',
        icon: <BookOpen className="w-8 h-8" />,
        color: '#059669',
        gradient: 'from-[#059669] to-[#065f46]',
        borderColor: 'border-[#059669]',
        pointsKey: 'solo.features',
        pricing: 'Included',
        pricingNote: 'With any active subscription',
    },
]

// ─── Sub-components ────────────────────────────────────────────────────────────
function StepIndicator({ current, total }: { current: number; total: number }) {
    return (
        <div className="flex items-center gap-3 justify-center mb-10">
            {Array.from({ length: total }).map((_, i) => (
                <div key={i} className="flex items-center">
                    <motion.div
                        initial={false}
                        animate={{
                            backgroundColor: i <= current ? '#00a3ff' : '#ffffff10',
                            scale: i === current ? 1.2 : 1,
                        }}
                        className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold text-white border border-white/10 shadow-lg"
                    >
                        {i < current ? <CheckCircle2 className="w-5 h-5" /> : i + 1}
                    </motion.div>
                    {i < total - 1 && (
                        <div className="w-12 h-0.5 mx-2 bg-white/10 overflow-hidden">
                            <motion.div
                                initial={{ x: '-100%' }}
                                animate={{ x: i < current ? '0%' : '-100%' }}
                                className="w-full h-full bg-[#00a3ff]"
                            />
                        </div>
                    )}
                </div>
            ))}
        </div>
    )
}

// ─── Main Component ────────────────────────────────────────────────────────────
export default function EducationSignupPage() {
    const [searchParams] = useSearchParams()
    const { t } = useTranslation()
    const navigate = useNavigate()

    // ── State ──
    const [step, setStep] = useState(0) // 0=choose hub, 1=details, 2=account, 3=success
    const [selectedHub, setSelectedHub] = useState<HubType | null>(null)
    const [hubName, setHubName] = useState('')
    const [adminName, setAdminName] = useState('')
    const [email, setEmail] = useState('')
    const [password, setPassword] = useState('')
    const [showPassword, setShowPassword] = useState(false)
    const { user, signUp, signIn } = useAuth()
    const [isLoading, setIsLoading] = useState(false)
    const [error, setError] = useState('')
    const [showEmailVerification, setShowEmailVerification] = useState(false)

    // Redirect if user is already authenticated
    React.useEffect(() => {
        if (user && !isLoading) {
            const redirectParam = searchParams.get('redirect')
            const targetPath = redirectParam ? decodeURIComponent(redirectParam) : (selectedHub === 'solo' ? '/learn' : '/education-admin')
            console.log('[EDU SIGNUP] User already authenticated, redirecting to', targetPath)
            navigate(targetPath, { replace: true })
        }
    }, [user, navigate, searchParams, isLoading, selectedHub])

    // Handle pre-selection from URL
    React.useEffect(() => {
        const hub = searchParams.get('hub') as HubType
        if (hub && HUB_PLANS.some(p => p.id === hub)) {
            setSelectedHub(hub)
            if (hub === 'solo') {
                setHubName('Solo Learning Account')
                setStep(2)
            } else {
                setStep(1)
            }
        }
    }, [searchParams])

    const selectedPlan = HUB_PLANS.find(p => p.id === selectedHub)

    // ── Handlers ──
    const handleHubSelect = (hubType: HubType) => {
        setSelectedHub(hubType)
        if (hubType === 'solo') {
            setHubName('Solo Learning Account')
            setStep(2)
        } else {
            setStep(1)
        }
    }

    const handleAccountSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        setError('')
        if (password.length < 8) {
            setError('Password must be at least 8 characters.')
            return
        }
        setIsLoading(true)
        try {
            // Solo learners sign up as plain users — no facility, no admin role.
            // Family/school hubs still get the education admin metadata.
            const isSolo = selectedHub === 'solo'
            const metaData = isSolo
                ? { name: adminName }
                : {
                    name: adminName,
                    is_education_admin: true,
                    is_family_admin: selectedHub === 'family_edu',
                    is_facility_admin: selectedHub === 'school',
                    education_hub_type: selectedHub,
                    hub_name: hubName,
                }

            const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
                email,
                password,
                options: {
                    data: {
                        name: adminName,
                        is_education_admin: true,
                        is_family_admin: selectedHub === 'family_edu',
                        is_facility_admin: selectedHub === 'school',
                        education_hub_type: selectedHub,
                        hub_name: hubName,
                    },
                    emailRedirectTo: `${window.location.origin}/auth/verify`,
                },
            })

            if (signUpError) {
                if (signUpError.message.includes('already registered')) {
                    console.log('[EDU SIGNUP] User already exists, attempting silent login...')
                    const { data: signInData, error: signInError } = await signIn(email, password)

                    if (!signInError && (signInData as any)?.user) {
                        console.log('[EDU SIGNUP] Silent login successful')
                        const redirectParam = searchParams.get('redirect')
                        const targetPath = redirectParam ? decodeURIComponent(redirectParam) : (isSolo ? '/learn' : '/education-admin')
                        navigate(targetPath, { replace: true })
                        return
                    }
                    setError('An account with this email already exists. Please sign in with your password.')
                } else throw signUpError
                return
            }
            if (!signUpData?.user?.email_confirmed_at) {
                setShowEmailVerification(true)
                return
            }
            setStep(3)
        } catch (err: any) {
            setError(err.message || 'Signup failed.')
        } finally {
            setIsLoading(false)
        }
    }

    // ── Email verification screen ──
    if (showEmailVerification) {
        return (
            <div className="min-h-screen bg-[#0f172a] flex items-center justify-center p-6 relative overflow-hidden">
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-blue-600/10 rounded-full blur-[120px] pointer-events-none" />
                <div className="relative z-10 backdrop-blur-md">
                    <EmailVerificationMessage email={email} onResendVerification={() => { }} isResending={false} />
                </div>
            </div>
        )
    }

    // ── Success screen ──
    if (step === 3) {
        return (
            <div className="min-h-screen bg-[#0f172a] flex items-center justify-center p-6 text-white text-center relative overflow-hidden">
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-blue-600/10 rounded-full blur-[120px] pointer-events-none" />
                <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="max-w-md w-full">
                    <div className="w-24 h-24 bg-gradient-to-br from-[#00a3ff] to-[#00a3ff] rounded-3xl mx-auto mb-8 flex items-center justify-center shadow-2xl shadow-blue-500/20">
                        <Trophy className="w-12 h-12 text-white" />
                    </div>
                    <h1 className="text-4xl font-black mb-4 tracking-tight">{t('education.welcome_to_the_academy')}</h1>
                    <p className="text-white/70 text-lg mb-10 leading-relaxed">
                        Your <strong>{hubName}</strong> is ready. Your journey into personalized, AI-powered learning starts now.
                    </p>
                    <Button onClick={() => { const rp = searchParams.get('redirect'); navigate(rp ? decodeURIComponent(rp) : (selectedHub === 'solo' ? '/learn' : '/education-admin')) }} size="xl" className="w-full h-16 rounded-2xl bg-[#00a3ff] hover:bg-[#2d44b8] text-xl font-bold shadow-xl border-0">
                        Enter Dashboard →
                    </Button>
                </motion.div>
            </div>
        )
    }

    return (
        <div className="min-h-screen bg-[#0f172a] text-white selection:bg-[#00a3ff]/30">
            {/* Header */}
            <header className="fixed top-0 left-0 right-0 z-50 py-6 px-10 flex items-center justify-between backdrop-blur-xl border-b border-white/5">
                <Link to="/" className="flex items-center gap-3 group">
                    <div className="w-10 h-10 bg-gradient-to-br from-[#00a3ff] to-[#00a3ff] rounded-xl flex items-center justify-center shadow-lg shadow-blue-500/20 group-hover:scale-110 transition-transform">
                        <GraduationCap className="w-6 h-6" />
                    </div>
                    <span className="text-2xl font-black tracking-tight">Still Waters Education</span>
                </Link>
                <Link to="/auth/login" className="text-sm font-bold text-white/50 hover:text-white transition-colors flex items-center gap-1 uppercase tracking-widest">
                    Already registered? <span className="text-[#00a3ff]">Sign In</span>
                </Link>
            </header>

            <main className="pt-32 pb-24 px-6 max-w-7xl mx-auto relative">
                {/* Decorative background blobs */}
                <div className="absolute top-1/4 -left-64 w-[500px] h-[500px] bg-blue-600/10 rounded-full blur-[120px] pointer-events-none" />
                <div className="absolute top-1/2 -right-64 w-[500px] h-[500px] bg-blue-600/10 rounded-full blur-[120px] pointer-events-none" />

                <AnimatePresence mode="wait">
                    {/* Step 0: Plan Selection */}
                    {step === 0 && (
                        <motion.div
                            key="step0"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.95 }}
                            className="space-y-16"
                        >
                            <div className="text-center space-y-6">
                                <motion.div
                                    initial={{ opacity: 0, scale: 0.8 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    transition={{ delay: 0.2 }}
                                    className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-blue-500/10 border border-blue-500/20 text-blue-400 text-xs font-black uppercase tracking-[0.2em]"
                                >
                                    <Sparkles className="w-4 h-4" /> The Future of Learning
                                </motion.div>
                                <h1 className="text-5xl md:text-7xl font-black tracking-tight leading-[1.05] max-w-4xl mx-auto">
                                    Every Student Deserves a <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#00a3ff] to-[#00a3ff]">{t('education.personal_tutor')}</span>
                                </h1>
                                <p className="text-xl text-white/60 max-w-2xl mx-auto leading-relaxed">
                                    Choose the hub that fits your needs. Our age-adaptive AI empowers educators, parents, and students with structured mastery paths.
                                </p>
                            </div>

                            <div className="grid md:grid-cols-3 gap-8">
                                {HUB_PLANS.map((plan) => (
                                    <button
                                        key={plan.id}
                                        onClick={() => handleHubSelect(plan.id)}
                                        className="group relative text-left p-1 rounded-[2.5rem] transition-all duration-500 hover:-translate-y-2"
                                    >
                                        <div className="absolute inset-0 bg-gradient-to-br opacity-0 group-hover:opacity-100 transition-opacity duration-500 blur-2xl -z-10"
                                            style={{ backgroundImage: `linear-gradient(to bottom right, ${plan.color}, transparent)` }} />

                                        <div className="h-full bg-white/5 border border-white/10 rounded-[2.4rem] p-10 backdrop-blur-3xl flex flex-col transition-colors group-hover:bg-white/10 hover:border-white/20">
                                            <div className="w-16 h-16 rounded-2xl flex items-center justify-center mb-8 shadow-inner"
                                                style={{ background: `linear-gradient(135deg, ${plan.color}20, ${plan.color}40)`, color: plan.color }}>
                                                {plan.icon}
                                            </div>

                                            <h3 className="text-2xl font-black mb-3">{plan.label}</h3>
                                            <p className="text-white/40 text-sm mb-10 leading-relaxed font-medium">{plan.tagline}</p>

                                            <div className="flex-1 space-y-4 mb-10">
                                                {/* Pull features from en.json based on id */}
                                                {(t(`homepage.${plan.pointsKey}`, { returnObjects: true }) as any[] || []).slice(0, 4).map((f: any, idx: number) => (
                                                    <div key={idx} className="flex gap-4 items-start text-sm text-white/70 font-medium">
                                                        <div className="w-5 h-5 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                                                            <CheckCircle2 className="w-3 h-3 text-[#00a3ff]" />
                                                        </div>
                                                        <span>{typeof f === 'string' ? f : f.title}</span>
                                                    </div>
                                                ))}
                                            </div>

                                            <div className="pt-8 border-t border-white/5 space-y-4">
                                                <div className="flex justify-between items-end">
                                                    <div>
                                                        <p className="text-3xl font-black">{plan.pricing}</p>
                                                        <p className="text-xs text-white/30 uppercase tracking-widest font-black mt-1">{plan.pricingNote}</p>
                                                    </div>
                                                    <div className="w-12 h-12 rounded-full border border-white/10 flex items-center justify-center group-hover:bg-white group-hover:text-black transition-all">
                                                        <ArrowRight className="w-5 h-5" />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </button>
                                ))}
                            </div>

                            {/* "How it Works" Preview Section */}
                            <section className="pt-20 border-t border-white/5">
                                <div className="max-w-4xl mx-auto space-y-16">
                                    <div className="text-center space-y-4">
                                        <h2 className="text-4xl font-black tracking-tight uppercase">{t('education.how_the_academy_works')}</h2>
                                        <p className="text-white/50 font-medium">{t('education.from_initial_setup_to_celebrating_mastery')}</p>
                                    </div>

                                    {/* Workflows for Schools/Families */}
                                    <div className="grid md:grid-cols-2 gap-16">
                                        {/* For Schools */}
                                        <div className="space-y-8 bg-white/5 p-8 rounded-[2rem] border border-white/10">
                                            <div className="flex items-center gap-3 text-[#00a3ff] font-black uppercase tracking-widest text-sm">
                                                <School size={18} /> For Educators
                                            </div>
                                            <div className="space-y-6">
                                                {(t('homepage.schools.howItWorks.steps', { returnObjects: true }) as any[] || []).map((step: any, i: number) => (
                                                    <div key={i} className="flex gap-6">
                                                        <span className="text-4xl font-black text-white/10">{step.num}</span>
                                                        <div>
                                                            <h4 className="font-bold text-lg mb-1">{step.title}</h4>
                                                            <p className="text-white/40 text-sm leading-relaxed">{step.desc}</p>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>

                                        {/* For Families */}
                                        <div className="space-y-8 bg-white/5 p-8 rounded-[2rem] border border-white/10">
                                            <div className="flex items-center gap-3 text-[#00a3ff] font-black uppercase tracking-widest text-sm">
                                                <Users size={18} /> For Families
                                            </div>
                                            <div className="space-y-6">
                                                {(t('homepage.families.howItWorks.steps', { returnObjects: true }) as any[] || []).map((step: any, i: number) => (
                                                    <div key={i} className="flex gap-6">
                                                        <span className="text-4xl font-black text-white/10">{step.num}</span>
                                                        <div>
                                                            <h4 className="font-bold text-lg mb-1">{step.title}</h4>
                                                            <p className="text-white/40 text-sm leading-relaxed">{step.desc}</p>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </section>

                            {/* Trust Signals */}
                            <div className="flex flex-wrap justify-center gap-10 py-10 opacity-40">
                                {[
                                    { icon: <ShieldCheck className="w-5 h-5" />, text: 'COPPA Ready' },
                                    { icon: <Activity className="w-5 h-5" />, text: 'Curriculum Aligned' },
                                    { icon: <Brain className="w-5 h-5" />, text: 'Mastery Tracking' },
                                    { icon: <Zap className="w-5 h-5" />, text: 'Instant Feedback' },
                                ].map((item, i) => (
                                    <div key={i} className="flex items-center gap-2 font-black text-xs uppercase tracking-widest">
                                        {item.icon} {item.text}
                                    </div>
                                ))}
                            </div>
                        </motion.div>
                    )}

                    {/* Step 1 & 2: Forms */}
                    {(step === 1 || step === 2) && selectedPlan && (
                        <motion.div
                            key="registration"
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            className="max-w-xl mx-auto"
                        >
                            <StepIndicator current={step} total={3} />

                            <div className="bg-white/5 border border-white/10 rounded-[2.5rem] p-12 backdrop-blur-3xl shadow-2xl relative">
                                <button onClick={() => setStep(prev => prev - 1)} className="absolute top-8 left-8 text-white/30 hover:text-white transition-colors">
                                    <ChevronLeft />
                                </button>

                                <div className="text-center space-y-4 mb-12">
                                    <div className="w-20 h-20 rounded-3xl mx-auto flex items-center justify-center shadow-xl mb-6" style={{ background: `linear-gradient(135deg, ${selectedPlan.color}, ${selectedPlan.color}dd)` }}>
                                        {selectedPlan.icon}
                                    </div>
                                    <h2 className="text-3xl font-black tracking-tight text-white leading-tight">
                                        {step === 1 ? `Setup Your ${selectedHub === 'school' ? 'Institution' : 'Family Hub'}` : 'Create Admin Account'}
                                    </h2>
                                    <p className="text-white/70 font-medium">{selectedPlan.tagline}</p>
                                </div>

                                {error && <div className="mb-8 p-4 bg-red-500/10 border border-red-500/20 text-red-400 rounded-xl text-sm font-bold text-center">{error}</div>}

                                <form onSubmit={step === 1 ? (e) => { e.preventDefault(); setStep(2) } : handleAccountSubmit} className="space-y-6">
                                    {step === 1 && (
                                        <>
                                            <div className="space-y-2">
                                                <label className="text-xs font-black uppercase tracking-widest text-white/40 ml-1">{selectedHub === 'school' ? 'Institution Name' : 'Hub Name'}</label>
                                                <input
                                                    type="text" value={hubName} onChange={e => setHubName(e.target.value)} required
                                                    placeholder={selectedHub === 'school' ? 'e.g. Westside High School' : 'e.g. The Miller Learning Hub'}
                                                    className="w-full h-16 bg-white/5 border border-white/10 rounded-2xl px-6 focus:ring-2 focus:ring-[#00a3ff] focus:border-transparent transition-all outline-none font-medium placeholder:text-white/20"
                                                />
                                            </div>
                                            <div className="space-y-2">
                                                <label className="text-xs font-black uppercase tracking-widest text-white/40 ml-1">{selectedHub === 'school' ? 'Principal Name' : 'Parent Name'}</label>
                                                <input
                                                    type="text" value={adminName} onChange={e => setAdminName(e.target.value)} required
                                                    placeholder="e.g. Jane Doe"
                                                    className="w-full h-16 bg-white/5 border border-white/10 rounded-2xl px-6 focus:ring-2 focus:ring-[#00a3ff] focus:border-transparent transition-all outline-none font-medium placeholder:text-white/20"
                                                />
                                            </div>
                                        </>
                                    )}

                                    {step === 2 && (
                                        <>
                                            <div className="space-y-2">
                                                <label className="text-xs font-black uppercase tracking-widest text-white/40 ml-1">{t('education.email_address')}</label>
                                                <input
                                                    type="email" value={email} onChange={e => setEmail(e.target.value)} required
                                                    placeholder="you@institution.edu"
                                                    className="w-full h-16 bg-white/5 border border-white/10 rounded-2xl px-6 focus:ring-2 focus:ring-[#00a3ff] focus:border-transparent transition-all outline-none font-medium placeholder:text-white/20"
                                                />
                                            </div>
                                            <div className="space-y-2">
                                                <label className="text-xs font-black uppercase tracking-widest text-white/40 ml-1">{t('education.secure_password')}</label>
                                                <div className="relative">
                                                    <input
                                                        type={showPassword ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} required
                                                        placeholder={t('education.min_characters')}
                                                        className="w-full h-16 bg-white/5 border border-white/10 rounded-2xl px-6 focus:ring-2 focus:ring-[#00a3ff] focus:border-transparent transition-all outline-none font-medium placeholder:text-white/20"
                                                    />
                                                    <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-6 top-1/2 -translate-y-1/2 text-white/30 hover:text-white transition-colors">
                                                        {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                                                    </button>
                                                </div>
                                            </div>
                                        </>
                                    )}

                                    <Button type="submit" disabled={isLoading} size="xl" className="w-full h-16 rounded-2xl bg-white text-black hover:bg-white/90 text-lg font-bold shadow-xl border-0 overflow-hidden group">
                                        {isLoading ? <Activity className="animate-spin" /> : (
                                            <span className="flex items-center gap-2">
                                                {step === 1 ? t('facility.signup.continue') : t('facility.signup.completeSetup')} <ArrowRight className="group-hover:translate-x-1 transition-transform" />
                                            </span>
                                        )}
                                    </Button>
                                </form>

                                {step === 2 && (
                                    <div className="mt-8 flex gap-4 p-5 bg-blue-500/5 border border-blue-500/10 rounded-2xl items-start">
                                        <Heart className="w-5 h-5 text-blue-400 flex-shrink-0 mt-1" />
                                        <p className="text-xs text-white/40 leading-relaxed">
                                            {t('facility.signup.termsNotice')}
                                        </p>
                                    </div>
                                )}

                                <div className="mt-8 text-center text-white/30 text-sm font-medium">
                                    {t('facility.signup.hasAccount')}{' '}
                                    <Link to="/auth/login" className="text-white hover:text-white/80 font-bold underline-offset-4 hover:underline transition-all">
                                        {t('facility.signup.signInLink')}
                                    </Link>
                                </div>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </main>
        </div>
    )
}
